import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { format } from "date-fns";
import { CalendarDays, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const OPTIONS = [
  { value: "present", label: "Present" },
  { value: "halfday", label: "Half day" },
  { value: "absent",  label: "Absent"  },
  { value: "leave",   label: "Leave"   },
];

function statusClass(v, active) {
  if (!active) return "text-muted-foreground";
  if (v === "present") return "bg-[hsl(var(--status-present))] text-white";
  if (v === "halfday") return "bg-[hsl(var(--status-halfday))] text-white";
  if (v === "absent")  return "bg-[hsl(var(--status-absent))] text-white";
  if (v === "leave")   return "bg-[hsl(var(--status-leave))] text-white";
  return "";
}

export default function AttendancePage() {
  const [date, setDate] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const isoDateStr = useMemo(() => isoDate(date), [date]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attendance?date=${isoDateStr}`);
      setRows(data.rows);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isoDateStr]);

  const mark = async (employee_id, status) => {
    setSavingId(employee_id);
    // optimistic update
    setRows((prev) => prev.map((r) => r.employee_id === employee_id ? { ...r, status } : r));
    try {
      await api.post("/attendance", { employee_id, date: isoDateStr, status });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally { setSavingId(null); }
  };

  const markAllPresent = async () => {
    if (rows.length === 0) return;
    const marks = rows.map((r) => ({ employee_id: r.employee_id, status: "present" }));
    setRows((prev) => prev.map((r) => ({ ...r, status: "present" })));
    try {
      await api.post("/attendance/bulk", { date: isoDateStr, marks });
      toast.success("All marked present");
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const counts = useMemo(() => {
    const c = { present: 0, halfday: 0, absent: 0, leave: 0, unmarked: 0 };
    rows.forEach((r) => {
      if (r.status === "present") c.present++;
      else if (r.status === "halfday") c.halfday++;
      else if (r.status === "absent") c.absent++;
      else if (r.status === "leave") c.leave++;
      else c.unmarked++;
    });
    return c;
  }, [rows]);

  return (
    <div className="p-8 lg:p-10" data-testid="attendance-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mini text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Roll call</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Daily attendance</h1>
          <p className="text-sm text-muted-foreground mt-2">Mark presence for the selected date. Changes save instantly.</p>
        </div>

        <div className="flex items-center gap-3">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="date-picker-btn"
                className="h-10 rounded-md gap-2"
              >
                <CalendarDays className="h-4 w-4" />
                <span className="font-mono-mini tabular-nums">{format(date, "EEE, MMM d, yyyy")}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="p-0 w-auto" data-testid="date-picker-popover">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { if (d) { setDate(d); setPickerOpen(false); } }}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button
            onClick={markAllPresent}
            disabled={rows.length === 0}
            className="h-10 btn-lift"
            data-testid="mark-all-present-btn"
          >
            Mark all present
          </Button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
        {[
          { k: "present",  label: "Present"  },
          { k: "halfday",  label: "Half day" },
          { k: "absent",   label: "Absent"   },
          { k: "leave",    label: "Leave"    },
          { k: "unmarked", label: "Unmarked" },
        ].map(({ k, label }) => (
          <Card
            key={k}
            className="p-4 rounded-2xl border-border shadow-soft"
            data-testid={`summary-${k}`}
          >
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
            <div className={`font-display text-3xl font-bold tabular-nums tracking-tight mt-2 ${
              k === "present" ? "text-[hsl(var(--status-present))]" :
              k === "halfday" ? "text-[hsl(var(--status-halfday))]" :
              k === "absent"  ? "text-[hsl(var(--status-absent))]" :
              k === "leave"   ? "text-[hsl(var(--status-leave))]" : "text-foreground"
            }`}>
              {counts[k]}
            </div>
          </Card>
        ))}
      </div>

      {/* Table */}
      <div className="mt-6 border border-border rounded-md bg-card overflow-hidden">
        <div className="grid grid-cols-[1fr_auto] items-center px-4 py-3 border-b border-border bg-secondary/60">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Employee</div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground pr-2">Status</div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-14 text-center">
            <Users className="h-6 w-6 mx-auto text-muted-foreground" />
            <div className="mt-3 text-sm text-muted-foreground">Add some employees before you can mark attendance.</div>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => {
              const initials = (r.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
              return (
                <li
                  key={r.employee_id}
                  className="grid grid-cols-[1fr_auto] items-center px-4 py-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
                  data-testid={`attendance-row-${r.employee_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {r.designation} · {r.department}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <ToggleGroup
                      type="single"
                      value={r.status || ""}
                      onValueChange={(v) => { if (v) mark(r.employee_id, v); }}
                      className="border border-border rounded-md overflow-hidden"
                    >
                      {OPTIONS.map(({ value, label }) => (
                        <ToggleGroupItem
                          key={value}
                          value={value}
                          data-testid={`attendance-toggle-${value}-${r.employee_id}`}
                          className={`px-3 h-9 text-xs font-medium rounded-none border-0 data-[state=on]:${
                            value === "present" ? "bg-[hsl(var(--status-present))]" :
                            value === "halfday" ? "bg-[hsl(var(--status-halfday))]" :
                            value === "absent"  ? "bg-[hsl(var(--status-absent))]" :
                            "bg-[hsl(var(--status-leave))]"
                          } data-[state=on]:text-white ${
                            r.status === value ? statusClass(value, true) : "hover:bg-black/5 dark:hover:bg-white/5"
                          }`}
                        >
                          {label}
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                    {savingId === r.employee_id && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
